import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/config";
import {
  Loader2, MapPin, Star, ShieldCheck, Sparkles, Users, Award,
  BadgeCheck, Ruler,
} from "lucide-react";

/**
 * iter153 — Property Verifying Card.
 *
 * Rich replacement for the plain spinner overlay:
 *   1. Live Google Maps satellite tile of the customer's property
 *      (uses google.maps JS API — the same one AddressAutocomplete /
 *      CustomerPropertyPreview already load — so no extra API activation
 *      is required).
 *   2. Animated scan-line overlay + corner brackets + pulsing pin.
 *   3. Progress bar (asymptotic — eases toward 92% until parent hides us).
 *   4. Status message cycles every 700 ms:
 *         Verifying your property…
 *         Measuring your mowable lawn area…
 *         Checking lot size…
 *         Calculating your custom price…
 *         Finding nearby completed properties…
 *         Preparing your instant quote…
 *   5. Below the map, "Nearby JDSN Lawn Care Customers":
 *         · Real display count (or "10+ customers in your area")
 *         · "Trusted by your neighbors" badge
 *         · Privacy-safe jittered markers (OSM tile with numbered pins)
 *         · Google rating, jobs completed nearby, in-business-since,
 *           Fully Insured + Free Estimates + Google-Verified badges
 *
 * Privacy: nearby markers come from /api/community/nearby-stats which
 * snaps every marker to a ~500 m grid and jitters by ±220 m. Customer
 * names and precise addresses are never exposed.
 */
export default function PropertyVerifyingCard({ property, visible = true }) {
  const [stats, setStats] = useState(null);
  const [messageIdx, setMessageIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const mounted = useRef(true);

  const lat = property?.lat;
  const lng = property?.lng;
  const address = property?.formatted_address || "your property";

  const MESSAGES = useMemo(
    () => [
      "Verifying your property…",
      "Measuring your mowable lawn area…",
      "Checking lot size…",
      "Calculating your custom price…",
      "Finding nearby completed properties…",
      "Preparing your instant quote…",
    ],
    [],
  );

  // ---- Cycle status messages every 700ms ----
  useEffect(() => {
    if (!visible) return;
    setMessageIdx(0);
    const t = setInterval(() => {
      setMessageIdx((i) => (i + 1) % MESSAGES.length);
    }, 700);
    return () => clearInterval(t);
  }, [visible, MESSAGES]);

  // ---- Animated progress bar ----
  useEffect(() => {
    if (!visible) return;
    setProgress(6);
    const t = setInterval(() => {
      setProgress((p) => {
        const target = 92;
        const step = Math.max(0.6, (target - p) * 0.09);
        return Math.min(target, p + step);
      });
    }, 120);
    return () => clearInterval(t);
  }, [visible]);

  // ---- Fetch nearby stats once we have coords ----
  useEffect(() => {
    mounted.current = true;
    if (!lat || !lng) return;
    let cancelled = false;
    api
      .get(`/community/nearby-stats?lat=${lat}&lng=${lng}`)
      .then(({ data }) => {
        if (!cancelled && mounted.current) setStats(data || null);
      })
      .catch(() => {
        if (!cancelled && mounted.current) {
          setStats({
            ok: true,
            display_count: "10+ customers in your area",
            markers: [],
            avg_google_rating: 4.9,
            in_business_since: 2022,
            years_in_business: Math.max(1, new Date().getFullYear() - 2022),
            insured: true,
            free_estimates: true,
          });
        }
      });
    return () => {
      cancelled = true;
      mounted.current = false;
    };
  }, [lat, lng]);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-emerald-950/85 backdrop-blur-md flex items-center justify-center px-4 py-6 overflow-y-auto"
      data-testid="property-verifying-card"
      role="alertdialog"
      aria-live="polite"
    >
      <div
        className="max-w-lg w-full bg-white rounded-3xl shadow-2xl overflow-hidden"
        style={{ animation: "verifyCardIn .35s cubic-bezier(.2,.9,.3,1.2)" }}
      >
        {/* ---- Property Satellite Header ---- */}
        <div
          className="relative w-full aspect-[16/9] bg-emerald-900"
          data-testid="property-verify-satellite"
        >
          <SatelliteCanvas lat={lat} lng={lng} property={property} />

          {/* Scan-line sweep */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div
              className="absolute inset-x-0 h-24 bg-gradient-to-b from-transparent via-emerald-300/40 to-transparent"
              style={{ animation: "verifyScan 2.4s linear infinite" }}
            />
            {/* Corner brackets */}
            <div className="absolute top-3 left-3 w-6 h-6 border-t-2 border-l-2 border-emerald-300/90 rounded-tl" />
            <div className="absolute top-3 right-3 w-6 h-6 border-t-2 border-r-2 border-emerald-300/90 rounded-tr" />
            <div className="absolute bottom-3 left-3 w-6 h-6 border-b-2 border-l-2 border-emerald-300/90 rounded-bl" />
            <div className="absolute bottom-3 right-3 w-6 h-6 border-b-2 border-r-2 border-emerald-300/90 rounded-br" />
          </div>

          {/* Live status chip */}
          <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-white/95 backdrop-blur px-3 py-1 rounded-full text-[11px] font-extrabold text-emerald-800 shadow-md flex items-center gap-1.5 z-10" data-testid="property-verify-status">
            <Loader2 className="w-3 h-3 animate-spin" />
            <span className="tabular-nums">{Math.round(progress)}%</span>
            <span className="opacity-40">·</span>
            <span>Scanning</span>
          </div>
        </div>

        {/* ---- Progress + cycling status message ---- */}
        <div className="px-6 pt-5 pb-4">
          <div className="h-1.5 w-full bg-emerald-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-[width] duration-[350ms] ease-out"
              style={{ width: `${progress}%` }}
              data-testid="property-verify-progress"
            />
          </div>

          <div className="mt-4 flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
              <Ruler className="w-5 h-5 text-emerald-700" />
            </div>
            <div className="flex-1 min-w-0">
              <div
                key={messageIdx}
                className="text-[15px] font-extrabold text-slate-900"
                style={{ animation: "verifyMsgIn .32s ease-out" }}
                data-testid="property-verify-message"
              >
                {MESSAGES[messageIdx]}
              </div>
              <div className="text-[12px] text-slate-500 truncate mt-0.5">
                {address}
              </div>
            </div>
          </div>
        </div>

        {/* ---- Nearby JDSN Customers ---- */}
        <div className="mx-6 mb-4 p-4 rounded-2xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100" data-testid="nearby-customers-panel">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-emerald-700" />
              <div className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-emerald-800">
                Nearby JDSN Lawn Care Customers
              </div>
            </div>
            <div className="px-2 py-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-extrabold uppercase tracking-wider flex items-center gap-1">
              <BadgeCheck className="w-3 h-3" />
              Trusted by your neighbors
            </div>
          </div>

          <div className="mt-3 text-[22px] font-black text-slate-900 tabular-nums" data-testid="nearby-customers-count">
            {stats?.display_count || "Counting nearby customers…"}
          </div>

          <NearbyMarkersMini stats={stats} lat={lat} lng={lng} />

          {/* Trust-signal grid */}
          <div className="mt-3 grid grid-cols-3 gap-2" data-testid="trust-signal-grid">
            <TrustStat
              icon={<Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />}
              label="Google Rating"
              value={stats?.avg_google_rating ? `${stats.avg_google_rating.toFixed(1)}★` : "4.9★"}
              testid="trust-stat-rating"
            />
            <TrustStat
              icon={<Award className="w-3.5 h-3.5 text-emerald-700" />}
              label="Jobs completed"
              value={
                stats?.nearby_customers && stats.nearby_customers >= 10
                  ? `${stats.nearby_customers}+ nearby`
                  : stats?.completed_jobs_total
                  ? `${stats.completed_jobs_total}+ total`
                  : "50+ total"
              }
              testid="trust-stat-jobs"
            />
            <TrustStat
              icon={<Sparkles className="w-3.5 h-3.5 text-emerald-700" />}
              label="In business since"
              value={String(stats?.in_business_since || 2022)}
              testid="trust-stat-since"
            />
          </div>

          <div className="mt-2.5 flex flex-wrap gap-1.5" data-testid="trust-badges">
            <Badge tone="emerald" testid="badge-insured">
              <ShieldCheck className="w-3 h-3" /> Fully Insured
            </Badge>
            <Badge tone="emerald" testid="badge-free-estimates">
              <BadgeCheck className="w-3 h-3" /> Free Estimates
            </Badge>
            <Badge tone="amber" testid="badge-google">
              <Star className="w-3 h-3 fill-amber-500 text-amber-500" /> Google-Verified
            </Badge>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes verifyCardIn {
          0% { opacity: 0; transform: translateY(12px) scale(.98); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes verifyScan {
          0%   { transform: translateY(-40%); opacity: 0; }
          10%  { opacity: 1; }
          90%  { opacity: 1; }
          100% { transform: translateY(140%); opacity: 0; }
        }
        @keyframes verifyPulse {
          0%   { transform: scale(1);   opacity: .55; }
          80%  { transform: scale(3.6); opacity: 0; }
          100% { transform: scale(3.6); opacity: 0; }
        }
        @keyframes verifyMsgIn {
          0%   { opacity: 0; transform: translateY(4px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

/**
 * Google Maps JS satellite canvas — uses the same google.maps JS API
 * that AddressAutocomplete already loaded. Falls back to OSM static
 * tiles if the JS API isn't available.
 */
function SatelliteCanvas({ lat, lng, property }) {
  const ref = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!key) { setReady(true); return; }
    if (window.google && window.google.maps) { setReady(true); return; }
    const existing = document.getElementById("gmaps-script");
    if (existing) {
      existing.addEventListener("load", () => setReady(true));
      return;
    }
    const s = document.createElement("script");
    s.id = "gmaps-script";
    s.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=geometry`;
    s.async = true;
    s.defer = true;
    s.onload = () => setReady(true);
    document.head.appendChild(s);
  }, []);

  useEffect(() => {
    if (!ready || !ref.current || !lat || !lng) return;
    if (!(window.google && window.google.maps)) return;
    const center = { lat: Number(lat), lng: Number(lng) };
    const map = new window.google.maps.Map(ref.current, {
      center,
      zoom: 19,
      mapTypeId: "satellite",
      tilt: 0,
      disableDefaultUI: true,
      gestureHandling: "none",
      keyboardShortcuts: false,
    });
    new window.google.maps.Marker({ position: center, map, title: "Your property" });

    // Highlight mowable area (green) if we already have it
    const mowable = Number(property?.mowable_sqft || 0);
    if (mowable > 0) {
      new window.google.maps.Circle({
        map, center,
        radius: Math.sqrt(mowable * 0.0929) * 0.6,   // sqft → sq m → radius
        strokeColor: "#10B981", strokeWeight: 2, strokeOpacity: 0.9,
        fillColor: "#10B981", fillOpacity: 0.35,
        clickable: false,
      });
    }
  }, [ready, lat, lng, property?.mowable_sqft]);

  // OSM fallback (no key needed) — used when Google Maps JS API can't load
  const apiKeyMissing = !process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (apiKeyMissing && lat && lng) {
    const osm = `https://staticmap.openstreetmap.de/staticmap.php?center=${lat},${lng}&zoom=18&size=600x340&maptype=mapnik&markers=${lat},${lng},red-pushpin`;
    return (
      <img
        src={osm}
        alt="Satellite view of your property"
        className="absolute inset-0 w-full h-full object-cover"
      />
    );
  }

  if (!lat || !lng) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <MapPin className="w-10 h-10 text-white/60" />
      </div>
    );
  }

  return (
    <div ref={ref} className="absolute inset-0 w-full h-full" />
  );
}

/**
 * Mini nearby-markers panel — pure SVG on a subtle map-tile background.
 * Uses the jittered/anonymized coordinates from the backend so each
 * dot's placement is deterministic-per-address but never reveals a
 * household. No external map tile service required.
 */
function NearbyMarkersMini({ stats, lat, lng }) {
  const markers = Array.isArray(stats?.markers) ? stats.markers.slice(0, 10) : [];

  if (!lat || !lng) {
    return (
      <div
        className="mt-3 h-24 relative rounded-xl bg-gradient-to-br from-emerald-100 to-emerald-50 border border-emerald-200/60 overflow-hidden"
        data-testid="nearby-markers-fallback"
      >
        <div className="absolute inset-0 flex items-center justify-center text-emerald-700 text-[11px] font-bold">
          Locating nearby customers…
        </div>
      </div>
    );
  }

  // Compute a projection window centered on the pin. Radius of ~3 mi in
  // degrees at Jacksonville's latitude — matches the backend query bounds.
  const R_LAT = 0.045;                    // ≈ 3.1 mi north-south
  const R_LNG = R_LAT / Math.cos((Number(lat) * Math.PI) / 180);
  const project = (mLat, mLng) => {
    const x = ((mLng - lng) / R_LNG + 0.5) * 100;
    const y = ((lat - mLat) / R_LAT + 0.5) * 100;
    return { x: Math.max(4, Math.min(96, x)), y: Math.max(6, Math.min(94, y)) };
  };

  return (
    <div
      className="mt-3 h-24 relative rounded-xl overflow-hidden border border-emerald-200/60 bg-gradient-to-br from-emerald-50 via-white to-emerald-50"
      data-testid="nearby-markers-map"
    >
      {/* Faux street-grid overlay (SVG lines) for a "map-y" feel */}
      <svg className="absolute inset-0 w-full h-full opacity-30" preserveAspectRatio="none" viewBox="0 0 100 100">
        <path d="M0 25 L100 22" stroke="#10b981" strokeWidth="0.4" strokeOpacity="0.6" fill="none" />
        <path d="M0 60 L100 66" stroke="#10b981" strokeWidth="0.4" strokeOpacity="0.6" fill="none" />
        <path d="M30 0 L26 100" stroke="#10b981" strokeWidth="0.4" strokeOpacity="0.6" fill="none" />
        <path d="M72 0 L76 100" stroke="#10b981" strokeWidth="0.4" strokeOpacity="0.6" fill="none" />
      </svg>

      {/* Nearby customer markers (privacy-safe, jittered by backend) */}
      {markers.map((m, i) => {
        const p = project(Number(m.lat), Number(m.lng));
        return (
          <div
            key={i}
            className="absolute w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-white shadow"
            style={{ left: `${p.x}%`, top: `${p.y}%`, transform: "translate(-50%, -50%)" }}
            data-testid={`nearby-marker-${i}`}
          />
        );
      })}

      {/* Center pin — the customer's property */}
      <div
        className="absolute w-3 h-3 rounded-full bg-red-500 ring-2 ring-white shadow-lg"
        style={{ left: "50%", top: "50%", transform: "translate(-50%, -50%)" }}
        data-testid="nearby-marker-center"
      />
      <div
        className="absolute w-3 h-3 rounded-full bg-red-500/40"
        style={{
          left: "50%", top: "50%", transform: "translate(-50%, -50%)",
          animation: "verifyPulse 1.8s ease-out infinite",
        }}
      />

      {/* Legend */}
      <div className="absolute bottom-1 left-1 right-1 flex items-center justify-between text-[9px] font-bold text-emerald-900/80 pointer-events-none">
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/80">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500" /> Your property
        </span>
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/80">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Nearby ~3 mi
        </span>
      </div>
    </div>
  );
}

function TrustStat({ icon, label, value, testid }) {
  return (
    <div
      className="rounded-xl bg-white border border-emerald-100 px-2 py-2 text-center"
      data-testid={testid}
    >
      <div className="flex items-center justify-center gap-1 text-[9px] uppercase tracking-wider text-emerald-800 font-bold">
        {icon}
        <span className="truncate">{label}</span>
      </div>
      <div className="mt-0.5 text-[13px] font-extrabold text-slate-900 truncate">
        {value}
      </div>
    </div>
  );
}

function Badge({ tone = "emerald", children, testid }) {
  const tones = {
    emerald: "bg-emerald-100 text-emerald-800 border-emerald-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-extrabold uppercase tracking-wider ${tones[tone]}`}
      data-testid={testid}
    >
      {children}
    </span>
  );
}
